/**
 * Who the reader was reading about, derived from the `?src=` guide slug.
 *
 * A reader arrives from "Deloitte internship guide" and meets a screen headed "What does a consulting
 * career actually look like?". The question is fine; the discontinuity is not. They searched a company,
 * clicked a page about that company, and landed somewhere that shows no sign of knowing it. This module
 * turns the slug we already carry for attribution into the one word that closes that gap.
 *
 * 🔴 IT NAMES THE READER'S CONTEXT, NEVER THE LAB'S CONTENT. The finance lab is not Deloitte's lab, and
 * a headline implying otherwise would be a lie told to a stranger in the first second of the
 * relationship. So the copy this feeds says "Reading about Deloitte?" — a true statement about what
 * they were just doing — and never "Deloitte's analysts work like this". See `preview-welcome.tsx`.
 *
 * WHY IT REFUSES MORE OFTEN THAN IT GUESSES. Roughly a third of the candidate guide pages are TOPIC
 * pages ("consulting-summer-2027-internship-guide"), not company pages. Personalising those produces
 * "Reading about Consulting?" on a consulting lab, which is worse than the generic headline: it looks
 * like a mail merge that misfired, and it tells the reader the personalisation is fake everywhere else
 * too. Returning null here is the good outcome, not a failure, and the welcome screen renders exactly
 * what it renders today.
 *
 * Plain module, no `'use client'`: pure string work over a value the client already resolved.
 */

import { DIRECT_GUIDE_SOURCE } from './guide-source';

/**
 * Slug tokens that describe the PAGE rather than its subject. Stripped from anywhere in the slug.
 *
 * These are the words pSEO titles are built from, so what survives is the subject itself. Seasons and
 * years are here because "summer 2027" is scaffolding on a guide title, not something to greet a reader
 * with.
 */
const PAGE_TOKENS: ReadonlySet<string> = new Set([
  'a',
  'an',
  'apply',
  'application',
  'applications',
  'best',
  'career',
  'careers',
  'fall',
  'for',
  'get',
  'guide',
  'guides',
  'how',
  'internship',
  'internships',
  'job',
  'jobs',
  'program',
  'programs',
  'requirements',
  'role',
  'roles',
  'salary',
  'spring',
  'students',
  'summer',
  'the',
  'to',
  'top',
  'undergraduate',
  'undergraduates',
  'winter',
]);

/**
 * Subject tokens that name a FIELD rather than an employer.
 *
 * A slug reducing to one of these is a topic page. Greeting its reader by the field is the mail-merge
 * failure described above, so these resolve to null and keep the generic headline.
 */
const FIELD_TOKENS: ReadonlySet<string> = new Set([
  'accounting',
  'analytics',
  'audit',
  'banking',
  'business',
  'consulting',
  'data',
  'engineering',
  'finance',
  'financial',
  'healthcare',
  'investment',
  'law',
  'management',
  'marketing',
  'nursing',
  'operations',
  'science',
  'strategy',
  'tech',
  'technology',
]);

/**
 * Brands whose written form is not what title-casing the slug produces.
 *
 * Keyed by the STEM (the slug with page tokens removed), so both `pwc-internship-guide` and
 * `pwc-summer-2027-internships` land on the same entry. Getting a company's own name wrong in a
 * headline addressed to someone who reads about that company is worse than not personalising at all,
 * which is why this list exists rather than a clever casing heuristic.
 */
const BRAND_NAMES: Readonly<Record<string, string>> = {
  bcg: 'BCG',
  'bank-of-america': 'Bank of America',
  bny: 'BNY',
  'capital-one': 'Capital One',
  ey: 'EY',
  'goldman-sachs': 'Goldman Sachs',
  'jp-morgan': 'JPMorgan',
  jpmorgan: 'JPMorgan',
  kpmg: 'KPMG',
  mckinsey: 'McKinsey',
  paypal: 'PayPal',
  pwc: 'PwC',
  rbc: 'RBC',
  't-mobile': 'T-Mobile',
  ubs: 'UBS',
};

/** A year like 2026 or 2027, which is page scaffolding rather than a subject. */
const YEAR_TOKEN_RE = /^(19|20)\d{2}$/;

/** Slug separators. Hoisted per `useTopLevelRegex`. */
const SLUG_SEPARATOR_RE = /[-/_]/;

/**
 * Longest stem we will treat as a name. Beyond this the slug is a sentence ("how-to-get-a-finance-
 * internship-with-no-experience"), and whatever survives token-stripping is not something to greet
 * anyone by.
 */
const MAX_NAME_TOKENS = 3;

/** Title-case one slug token. ASCII only, which is what the slug regex in `guide-source.ts` allows. */
function capitalise(token: string): string {
  return token.charAt(0).toUpperCase() + token.slice(1);
}

/**
 * The subject of a guide page, ready to render, or null when there is not a confident one.
 *
 * Null is the common and correct answer for topic pages, direct hits and anything unrecognised. Every
 * caller must render its existing copy in that case rather than a placeholder.
 */
export function guideAudienceName(guideSource: string): string | null {
  // A direct hit, a share or a bookmark. There is no guide page and therefore nobody to name — and the
  // sentinel is itself a plausible-looking token, so without this it would title-case into "Direct".
  if (guideSource === DIRECT_GUIDE_SOURCE) {
    return null;
  }

  const tokens = guideSource
    .split(SLUG_SEPARATOR_RE)
    .map((token) => token.trim())
    .filter((token) => token.length > 0 && !(PAGE_TOKENS.has(token) || YEAR_TOKEN_RE.test(token)));

  if (tokens.length === 0 || tokens.length > MAX_NAME_TOKENS) {
    return null;
  }

  const stem = tokens.join('-');
  const brand = BRAND_NAMES[stem];

  if (brand) {
    return brand;
  }

  // Every surviving token names a field, so this is a topic page. The generic headline is the right one.
  if (tokens.every((token) => FIELD_TOKENS.has(token))) {
    return null;
  }

  return tokens.map(capitalise).join(' ');
}
